import { ArrowUpIcon } from 'lucide-react';
import { Button } from './button';
import { cn } from '@lib/utils';

export interface QuestionInteractionRowProps {
  upliftsCount: number;
  meTooCount: number;
  iCanHelpCount: number;
  userHasUpvoted: boolean;
  userHasMeToo: boolean;
  userHasICanHelp: boolean;
  upliftLimitReached: boolean;
  isOwnQuestion: boolean;
  isPending: boolean;
  onUpvote: () => void;
  onMeToo: () => void;
  onCanHelp: () => void;
}

export const QuestionInteractionRow = ({
  upliftsCount,
  meTooCount,
  iCanHelpCount,
  userHasUpvoted,
  userHasMeToo,
  userHasICanHelp,
  upliftLimitReached,
  isOwnQuestion,
  isPending,
  onUpvote,
  onMeToo,
  onCanHelp,
}: QuestionInteractionRowProps) => (
  <div className="flex items-center justify-between gap-2">
    <Button
      variant="outline"
      onClick={e => {
        e.stopPropagation();
        onUpvote();
      }}
      disabled={isPending || isOwnQuestion || upliftLimitReached}
      className={cn(
        'h-[38px] px-3 py-[5px] rounded-[25px] border-2 border-mb-bg bg-transparent transition-colors',
        userHasUpvoted && 'bg-blue-50 border-blue-200 text-blue-600',
        isOwnQuestion && 'opacity-50 cursor-not-allowed',
      )}
    >
      <ArrowUpIcon
        className={cn('w-4 h-4 mr-2', userHasUpvoted && 'text-blue-600')}
        aria-hidden="true"
      />
      {/* Accessible name is built by concatenating the visible text
          children. Without the sr-only "Uplift, " prefix, a screen reader
          or voice-control user would hear only the count number ("3")
          as this button's name — not enough to know what action tapping
          it would take. The count stays visible for sighted users and
          also appears in the accessible name (sr-only + visible span
          concatenate), so AT users hear "Uplift, 3". */}
      <span className="sr-only">Uplift, </span>
      <span className="font-medium text-sm">{upliftsCount}</span>
    </Button>
    <Button
      variant="outline"
      onClick={e => {
        e.stopPropagation();
        onMeToo();
      }}
      disabled={isPending || isOwnQuestion}
      className={cn(
        'hover:bg-orange-50 h-[38px] px-3 py-[5px] rounded-[25px] bg-white shadow-soft border-0 transition-colors',
        userHasMeToo && 'bg-orange-50 text-orange-600',
        isOwnQuestion && 'opacity-50 cursor-not-allowed',
      )}
    >
      {/* Decorative icon — the visible "Me too" label IS the accessible
          name for this button, so leave alt="" so screen readers don't
          announce it twice. */}
      <img
        src="/Metoo (1).svg"
        alt=""
        aria-hidden="true"
        className={cn('w-6 h-6 mr-1', userHasMeToo && 'filter-orange')}
      />
      <span className="font-normal text-sm mr-1">Me too</span>
      {meTooCount > 0 && <span className="font-medium text-sm">{meTooCount}</span>}
    </Button>
    <Button
      variant="outline"
      onClick={e => {
        e.stopPropagation();
        onCanHelp();
      }}
      disabled={isPending || isOwnQuestion}
      className={cn(
        'h-[38px] px-3 py-[5px] rounded-[25px] shadow-soft border-0 transition-colors',
        userHasICanHelp
          ? 'bg-green-50 text-green-600'
          : 'bg-white hover:bg-green-50 hover:text-green-600',
        isOwnQuestion && 'opacity-50 cursor-not-allowed',
      )}
    >
      {/* Decorative icon — see Me too rationale above. */}
      <img src="/I Can Help.svg" alt="" aria-hidden="true" className="w-6 h-6 mr-1" />
      <span className="font-normal text-sm mr-1">I can help</span>
      {iCanHelpCount > 0 && <span className="font-medium text-sm">{iCanHelpCount}</span>}
    </Button>
  </div>
);
